import React, { useEffect, useState } from 'react';

type Locale = 'pl' | 'en' | 'es';

type ConsentSettings = {
  analytics: boolean;
  marketing: boolean;
};

type CookieConsentBannerProps = {
  locale: Locale;
  onChangeLocale: (locale: Locale) => void;
  onOpenPrivacy: () => void;
};

const STORAGE_KEY = 'pienkowski_cookie_consent';

const text = {
  pl: {
    kicker: 'Prywatność i cookies',
    title: 'Ta strona korzysta z plików cookies',
    intro:
      'Strona pienkowskilegal.pl używa plików cookies niezbędnych do jej prawidłowego działania. Za Twoją zgodą strona może korzystać także z cookies analitycznych lub marketingowych, w tym związanych z Google Analytics, Google Tag Manager oraz Google Ads.',
    small:
      'Możesz zaakceptować wszystkie cookies opcjonalne, odrzucić je albo dostosować swoje ustawienia.',
    requiredTitle: 'Cookies niezbędne',
    requiredText: 'Są wymagane do prawidłowego działania strony.',
    requiredBadge: 'Zawsze aktywne',
    analyticsTitle: 'Cookies analityczne',
    analyticsText: 'Pomagają analizować statystyki odwiedzin i działanie strony.',
    marketingTitle: 'Cookies marketingowe',
    marketingText: 'Służą do pomiaru skuteczności reklam i konwersji.',
    reject: 'Odrzuć',
    customize: 'Dostosuj',
    save: 'Zapisz wybór',
    accept: 'Akceptuj',
    privacy: 'Polityka prywatności i cookies',
  },
  en: {
    kicker: 'Privacy and cookies',
    title: 'This website uses cookies',
    intro:
      'The pienkowskilegal.pl website uses cookies necessary for its proper functioning. With your consent, the website may also use analytical or marketing cookies, including cookies related to Google Analytics, Google Tag Manager and Google Ads.',
    small:
      'You can accept all optional cookies, reject them or customize your settings.',
    requiredTitle: 'Necessary cookies',
    requiredText: 'They are required for the website to function properly.',
    requiredBadge: 'Always active',
    analyticsTitle: 'Analytical cookies',
    analyticsText: 'They help analyse visit statistics and the functioning of the website.',
    marketingTitle: 'Marketing cookies',
    marketingText: 'They are used to measure advertising effectiveness and conversions.',
    reject: 'Reject',
    customize: 'Customize',
    save: 'Save choice',
    accept: 'Accept',
    privacy: 'Privacy policy and cookies',
  },
  es: {
    kicker: 'Privacidad y cookies',
    title: 'Este sitio web utiliza cookies',
    intro:
      'El sitio web pienkowskilegal.pl utiliza cookies necesarias para su correcto funcionamiento. Con su consentimiento, el sitio también puede utilizar cookies analíticas o de marketing, incluidas las relacionadas con Google Analytics, Google Tag Manager y Google Ads.',
    small:
      'Puede aceptar todas las cookies opcionales, rechazarlas o personalizar la configuración.',
    requiredTitle: 'Cookies necesarias',
    requiredText: 'Son necesarias para el correcto funcionamiento del sitio.',
    requiredBadge: 'Siempre activas',
    analyticsTitle: 'Cookies analíticas',
    analyticsText: 'Ayudan a analizar las estadísticas de visitas y el funcionamiento del sitio.',
    marketingTitle: 'Cookies de marketing',
    marketingText: 'Se utilizan para medir la eficacia de los anuncios y las conversiones.',
    reject: 'Rechazar',
    customize: 'Personalizar',
    save: 'Guardar selección',
    accept: 'Aceptar',
    privacy: 'Política de privacidad y cookies',
  },
};

const updateGoogleConsent = (settings: ConsentSettings) => {
  window.dataLayer = window.dataLayer || [];

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: settings.analytics ? 'granted' : 'denied',
      ad_storage: settings.marketing ? 'granted' : 'denied',
      ad_user_data: settings.marketing ? 'granted' : 'denied',
      ad_personalization: settings.marketing ? 'granted' : 'denied',
    });
  }

  window.dataLayer.push({
    event: 'cookie_consent_update',
    cookie_consent_analytics: settings.analytics ? 'granted' : 'denied',
    cookie_consent_marketing: settings.marketing ? 'granted' : 'denied',
  });
};

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  locale,
  onChangeLocale,
  onOpenPrivacy,
}) => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const copy = text[locale] ?? text.pl;

  useEffect(() => {
    const savedConsent = localStorage.getItem(STORAGE_KEY);

    if (!savedConsent) {
      setVisible(true);
      return;
    }

    try {
      const parsedConsent = JSON.parse(savedConsent) as ConsentSettings;
      setAnalytics(parsedConsent.analytics);
      setMarketing(parsedConsent.marketing);
      updateGoogleConsent(parsedConsent);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    const openSettings = () => {
      const savedConsent = localStorage.getItem(STORAGE_KEY);

      if (savedConsent) {
        try {
          const parsedConsent = JSON.parse(savedConsent) as ConsentSettings;
          setAnalytics(parsedConsent.analytics);
          setMarketing(parsedConsent.marketing);
        } catch {
          setAnalytics(false);
          setMarketing(false);
        }
      }

      setShowSettings(true);
      setVisible(true);
    };

    window.addEventListener('cookies:settings', openSettings);

    return () => {
      window.removeEventListener('cookies:settings', openSettings);
    };
  }, []);

  const saveConsent = (settings: ConsentSettings) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    updateGoogleConsent(settings);
    setVisible(false);
  };

  const acceptAll = () => {
    saveConsent({
      analytics: true,
      marketing: true,
    });
  };

  const rejectOptional = () => {
    saveConsent({
      analytics: false,
      marketing: false,
    });
  };

  const saveSelected = () => {
    saveConsent({
      analytics,
      marketing,
    });
  };

  const openPrivacyPolicy = () => {
    onOpenPrivacy();
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="cookie-consent-overlay" role="dialog" aria-modal="true" aria-labelledby="cookie-consent-title">
      <div className="cookie-consent-box">
        <div className="cookie-consent-content">
          <div className="cookie-consent-top">
            <p className="cookie-consent-kicker">{copy.kicker}</p>

            <div className="cookie-consent-language" aria-label="Wybór języka">
              <button
                type="button"
                className={locale === 'pl' ? 'is-active' : ''}
                onClick={() => onChangeLocale('pl')}
              >
                PL
              </button>
              <button
                type="button"
                className={locale === 'en' ? 'is-active' : ''}
                onClick={() => onChangeLocale('en')}
              >
                EN
              </button>
              <button
                type="button"
                className={locale === 'es' ? 'is-active' : ''}
                onClick={() => onChangeLocale('es')}
              >
                ES
              </button>
            </div>
          </div>

          <h2 id="cookie-consent-title">{copy.title}</h2>

          <p>{copy.intro}</p>

          {!showSettings && <p className="cookie-consent-small">{copy.small}</p>}

          {showSettings && (
            <div className="cookie-consent-settings">
              <div className="cookie-consent-option">
                <div>
                  <strong>{copy.requiredTitle}</strong>
                  <p>{copy.requiredText}</p>
                </div>
                <span className="cookie-consent-required">{copy.requiredBadge}</span>
              </div>

              <label className="cookie-consent-option">
                <div>
                  <strong>{copy.analyticsTitle}</strong>
                  <p>{copy.analyticsText}</p>
                </div>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(event) => setAnalytics(event.target.checked)}
                />
              </label>

              <label className="cookie-consent-option">
                <div>
                  <strong>{copy.marketingTitle}</strong>
                  <p>{copy.marketingText}</p>
                </div>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(event) => setMarketing(event.target.checked)}
                />
              </label>
            </div>
          )}

          <div className="cookie-consent-actions">
            <button type="button" className="cookie-btn cookie-btn-secondary" onClick={rejectOptional}>
              {copy.reject}
            </button>

            {!showSettings ? (
              <button type="button" className="cookie-btn cookie-btn-outline" onClick={() => setShowSettings(true)}>
                {copy.customize}
              </button>
            ) : (
              <button type="button" className="cookie-btn cookie-btn-outline" onClick={saveSelected}>
                {copy.save}
              </button>
            )}

            <button type="button" className="cookie-btn cookie-btn-primary" onClick={acceptAll}>
              {copy.accept}
            </button>
          </div>

          <button type="button" className="cookie-consent-link" onClick={openPrivacyPolicy}>
            {copy.privacy}
          </button>
        </div>
      </div>
    </div>
  );
};

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}
